/**
 * Index in the shared control-events buffer where the karel inter-step
 * sleep time is written. Paired reader is `get_sleep_time` in
 * `KarelLib/python/karel/stanfordkarel.py`. Stored as a single byte
 * (0..255), where 255 means "sleep ~1 second per step" and 0 means "no
 * sleep".
 *
 * The control-events buffer is shared across libraries (graphics also
 * uses index 0 for mousedown.x), so this assignment relies on a karel
 * program never coexisting with a graphics program in the same worker
 * run. A future change should give each library its own namespaced slot.
 *
 * TODO: [#2470](https://github.com/CodeinPlace/cip/issues/2470) - replace
 * with a shared/autogenerated byte-index schema across libraries.
 */
export const KAREL_SLEEP_TIME_BYTE_INDEX = 0;

/**
 * Maximum encoded sleep-time byte sent over the control-events buffer.
 * The byte is interpreted by `stanfordkarel.get_sleep_time` as a
 * fraction of a second via `byte / 255.0`, so 255 ↔ 1s and 0 ↔ no sleep.
 * The TS-side encode here is the inverse — a single conversion paired
 * with the Python decode, no double scaling.
 */
const SLEEP_TIME_BYTE_MAX = 255;

/**
 * Encode a karel inter-step sleep time (seconds, clamped to the closed
 * interval [0, 1]) to its byte representation. Out-of-range inputs are
 * clamped, not rejected; the result is therefore in [0, 255] without a
 * second clamp.
 *
 * Internal helper for {@link BasePlug.setKarelSleepTime} and the karel
 * pre-run hook it registers. Not part of the package's public API —
 * external consumers should call `plug.setKarelSleepTime(seconds)`
 * instead, which handles clamp + byte write + persistent re-assertion
 * across runs in one call.
 *
 * @param seconds Inter-step sleep time in the closed interval [0, 1].
 * @return Byte value in [0, 255] suitable for `setControlEventsBuffer`.
 */
export function encodeKarelSleepByte(seconds: number): number {
  const clamped = Math.min(1, Math.max(0, seconds));
  return Math.round(clamped * SLEEP_TIME_BYTE_MAX);
}
